import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpLeft,
  ImageOff,
  Sparkles,
  Users,
  Zap,
  ListChecks,
} from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import ToolLogo from "@/components/ToolLogo";
import SaveButton from "@/components/SaveButton";
import { useTools } from "@/content/ContentProvider";
import { pickLang, useLanguage, langPrefix } from "@/i18n/LanguageContext";
import { getToolNameAr } from "@/data/toolNamesAr";
import { trackEvent } from "@/analytics/ga";

/**
 * ToolDetail — public landing page for a single tool at `/tools/:slug`
 * (and `/ar/tools/:slug` for Arabic).
 *
 * Sections, in order:
 *   1. Breadcrumb (Tools → Category → Name)
 *   2. Hero card: logo (96px) + bilingual name + categories + Try Tool CTA + Save
 *   3. Optional screenshot/preview image
 *   4. "About" — full description
 *   5. "Best for" — audience block
 *   6. "When to use it" — guidance block
 *   7. "How to start" — bilingual paragraph (admin-editable)
 *   8. Related tools — same-category tools first, capped to 4
 *   9. Footer: Back to tools + secondary related-tool link
 *
 * SEO:
 *   - Updates `<title>`, `<meta name="description">`, OG title/description on
 *     mount; restores on unmount.
 *   - Slug-based URL is sitemap-eligible (Phase 19f).
 *
 * Resilience:
 *   - All admin-optional fields (`screenshotUrl`, `howToStart`, `audience`,
 *     `whenToUse`) gracefully collapse when empty, so existing seed tools
 *     without these fields still render a valid page.
 */

const LABELS = {
  ar: {
    crumbAria: "فتات الخبز",
    crumbTools: "الأدوات",
    free: "مجاني",
    tryCta: "جرّب الأداة",
    aboutTitle: "عن الأداة",
    bestForTitle: "مناسب لـ",
    whenToUseTitle: "متى نرشّحه",
    howToStartTitle: "كيف تبدأ",
    howToStartLead:
      "خطوات سريعة وعملية لتجربة الأداة من أوّل استخدام دون الحاجة إلى اشتراك مدفوع.",
    relatedTitle: "أدوات ذات صلة",
    relatedDesc: "أدوات أخرى من الدليل في نفس القسم — قد تناسب احتياجك أيضًا.",
    back: "العودة إلى دليل الأدوات",
    notFoundTitle: "تعذّر العثور على الأداة",
    coverFallbackText: "لا توجد لقطة للأداة بعد",
    brand: "مجتمع الذكاء الاصطناعي السوري",
  },
  en: {
    crumbAria: "Breadcrumb",
    crumbTools: "Tools",
    free: "Free",
    tryCta: "Try Tool",
    aboutTitle: "About",
    bestForTitle: "Best for",
    whenToUseTitle: "When we recommend it",
    howToStartTitle: "How to start",
    howToStartLead:
      "Quick, practical steps to start using this tool without a paid subscription.",
    relatedTitle: "Related tools",
    relatedDesc:
      "Other tools from the directory in the same category — may also fit your need.",
    back: "Back to tools directory",
    notFoundTitle: "Tool not found",
    coverFallbackText: "No screenshot yet",
    brand: "Syrian AI Society",
  },
};

/* ------------------------------------------------------------------ */
/* SEO meta helpers                                                   */
/* ------------------------------------------------------------------ */
function upsertMeta(attr, name, content) {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}
function removeMetaIfOwned(el) {
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function ToolDetail() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const l = LABELS[lang];
  const {
    getToolBySlug,
    getToolsByCategory,
    primaryCategoryOf,
    toolsLoaded,
  } = useTools();

  const tool = getToolBySlug(slug);

  // Hooks must run unconditionally — keep the SEO effect above any early
  // returns so React's hook order is stable across renders.
  useEffect(() => {
    if (!tool) return undefined;
    const enName = tool.name || "";
    const arName = getToolNameAr(tool.slug) || enName;
    const displayName = lang === "ar" ? arName : enName;
    const desc = pickLang(tool.desc, lang) || "";
    const title = `${displayName} — ${l.crumbTools} | ${l.brand}`;
    const description = desc.slice(0, 160);

    const previousTitle = document.title;
    document.title = title;
    const descEl = upsertMeta("name", "description", description);
    const ogTitleEl = upsertMeta("property", "og:title", title);
    const ogDescEl = upsertMeta("property", "og:description", description);
    const ogTypeEl = upsertMeta("property", "og:type", "website");
    const ogImgEl = tool.screenshotUrl
      ? upsertMeta("property", "og:image", tool.screenshotUrl)
      : null;

    return () => {
      document.title = previousTitle;
      if (descEl) descEl.setAttribute("content", "");
      removeMetaIfOwned(ogTitleEl);
      removeMetaIfOwned(ogDescEl);
      removeMetaIfOwned(ogTypeEl);
      removeMetaIfOwned(ogImgEl);
    };
  }, [tool, lang, l.brand, l.crumbTools]);

  // GA4: track a `tool_view` event once per slug-mount. Mirrors the
  // standard `view_item` content event but uses our own name so the
  // GA4 property's "Engagement → Events" report stays self-explanatory.
  // The page_view event is fired separately by the central
  // AnalyticsBridge — keeping the two distinct lets us answer
  // "how many people actually landed on a tool detail page" vs.
  // "how many bots/crawlers / quick clicks scrolled past".
  useEffect(() => {
    if (!tool) return;
    trackEvent("tool_view", {
      tool_slug: tool.slug,
      tool_name: tool.name,
      categories: Array.isArray(tool.categories)
        ? tool.categories.join(",")
        : "",
    });
  }, [tool]);

  // Show a small skeleton while ContentProvider's first fetch is in flight.
  // Without this, a slug that DOES exist would briefly hit the "not found"
  // redirect during the initial seed-data window before the API responds.
  if (!toolsLoaded && !tool) {
    return (
      <section className="sas-section" data-testid="tool-detail-loading">
        <div className="sas-container">
          <div className="h-6 w-40 bg-[color:var(--sas-accent-soft)] rounded animate-pulse" />
          <div className="mt-6 h-12 w-2/3 bg-[color:var(--sas-accent-soft)] rounded animate-pulse" />
          <div className="mt-10 h-72 bg-[color:var(--sas-accent-soft)] rounded-2xl animate-pulse" />
        </div>
      </section>
    );
  }

  if (!tool) {
    return <Navigate to={`${langPrefix(lang)}/tools` || "/tools"} replace />;
  }

  return <ToolDetailView tool={tool} lang={lang} l={l}
    getToolsByCategory={getToolsByCategory}
    primaryCategoryOf={primaryCategoryOf}
  />;
}

/* ------------------------------------------------------------------ */
/* View — split out so we can keep early-return logic clean above.     */
/* ------------------------------------------------------------------ */
function ToolDetailView({ tool, lang, l, getToolsByCategory, primaryCategoryOf }) {
  const enName = tool.name || "";
  const arName = getToolNameAr(tool.slug) || "";
  const showSecondaryName =
    !!arName && arName !== enName; // suppress duplicate when AR name === EN

  const desc = pickLang(tool.desc, lang);
  const audience = pickLang(tool.audience, lang);
  const whenToUse = pickLang(tool.whenToUse, lang);
  const howToStart = pickLang(tool.howToStart, lang);
  const screenshotUrl = (tool.screenshotUrl || "").trim();

  const primaryCat = primaryCategoryOf(tool);
  const primaryCatLabel = pickLang(primaryCat?.label, lang);

  // Same-category sibling tools (excluding the current one), capped to 4
  // so the related grid never runs longer than a single screen row.
  const related = useMemo(() => {
    if (!primaryCat) return [];
    const siblings = getToolsByCategory(primaryCat.id) || [];
    return siblings.filter((t) => t.slug !== tool.slug).slice(0, 4);
  }, [primaryCat, getToolsByCategory, tool.slug]);

  const BackIcon = lang === "ar" ? ArrowLeft : ArrowRight;

  return (
    <article data-testid={`tool-detail-${tool.slug}`}>
      {/* ------------- Breadcrumb + hero ------------- */}
      <header
        className="sas-section-sm border-b border-[color:var(--sas-line)]"
        data-testid="tool-detail-header"
      >
        <div className="sas-container">
          <nav
            className="text-xs text-[color:var(--sas-ink-2)] mb-6"
            aria-label={l.crumbAria}
          >
            <LocalizedLink
              to="/tools"
              className="hover:text-[color:var(--sas-accent)]"
              data-testid="tool-detail-crumb-tools"
            >
              {l.crumbTools}
            </LocalizedLink>
            {primaryCatLabel && (
              <>
                <span className="mx-2" aria-hidden>
                  /
                </span>
                <span>{primaryCatLabel}</span>
              </>
            )}
          </nav>

          <div className="flex flex-col md:flex-row md:items-start gap-6">
            {/* Logo */}
            <ToolLogo
              tool={tool}
              size={96}
              testId={`tool-detail-logo-${tool.slug}`}
              className=""
            />

            {/* Title block + meta */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {primaryCatLabel && (
                  <span className="sas-chip">{primaryCatLabel}</span>
                )}
                <span
                  className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full"
                  style={{
                    background: "var(--sas-accent-soft)",
                    color: "var(--sas-accent)",
                  }}
                  title={l.free}
                  data-testid={`tool-detail-free-${tool.slug}`}
                >
                  <Sparkles size={11} aria-hidden />
                  {l.free}
                </span>
              </div>

              <h1
                className="sas-h1 leading-tight break-words"
                dir="ltr"
                data-testid={`tool-detail-name-en-${tool.slug}`}
              >
                {enName}
              </h1>
              {showSecondaryName && (
                <div
                  className="mt-2 text-[15px] text-[color:var(--sas-ink-2)]"
                  data-testid={`tool-detail-name-ar-${tool.slug}`}
                >
                  <span dir="rtl" lang="ar">{arName}</span>
                </div>
              )}

              {desc && (
                <p
                  className="sas-lead mt-5 max-w-3xl"
                  data-testid={`tool-detail-desc-${tool.slug}`}
                >
                  {desc}
                </p>
              )}

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href={tool.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="sas-btn sas-btn-primary"
                  data-testid={`tool-detail-cta-${tool.slug}`}
                  onClick={() =>
                    trackEvent("tool_open", {
                      tool_slug: tool.slug,
                      tool_name: tool.name,
                      source: "detail_page",
                    })
                  }
                >
                  <span>{l.tryCta}</span>
                  <ArrowUpLeft size={16} aria-hidden />
                </a>
                <SaveButton
                  itemType="tool"
                  itemSlug={tool.slug}
                  itemId={tool.id}
                  variant="inline"
                  testId={`tool-detail-save-${tool.slug}`}
                  stopPropagation={false}
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ------------- Screenshot / preview ------------- */}
      {screenshotUrl && (
        <section className="sas-section-sm" data-testid="tool-detail-screenshot-section">
          <div className="sas-container">
            <div className="max-w-4xl mx-auto">
              <ScreenshotFrame src={screenshotUrl} alt={`${enName} screenshot`} l={l} />
            </div>
          </div>
        </section>
      )}

      {/* ------------- Body: about / audience / when / how to start ------------- */}
      <section className="pb-16" data-testid="tool-detail-body">
        <div className="sas-container">
          <div className="max-w-3xl mx-auto flex flex-col gap-12">
            {desc && (
              <DetailBlock
                icon={<Sparkles size={16} aria-hidden />}
                title={l.aboutTitle}
                testId="tool-detail-about"
              >
                <p className="sas-body text-[15.5px] leading-relaxed">{desc}</p>
              </DetailBlock>
            )}

            {audience && (
              <DetailBlock
                icon={<Users size={16} aria-hidden />}
                title={l.bestForTitle}
                testId="tool-detail-audience"
              >
                <p className="sas-body text-[15.5px] leading-relaxed">{audience}</p>
              </DetailBlock>
            )}

            {whenToUse && (
              <DetailBlock
                icon={<Zap size={16} aria-hidden />}
                title={l.whenToUseTitle}
                testId="tool-detail-when"
              >
                <p className="sas-body text-[15.5px] leading-relaxed">{whenToUse}</p>
              </DetailBlock>
            )}

            {howToStart && (
              <DetailBlock
                icon={<ListChecks size={16} aria-hidden />}
                title={l.howToStartTitle}
                testId="tool-detail-how"
              >
                <p className="text-[13px] text-[color:var(--sas-ink-2)] mb-4">
                  {l.howToStartLead}
                </p>
                {/* Render multi-line content as paragraphs / list-like
                    blocks. Admins can use blank lines or "1. " prefixes
                    in the textarea — we render each non-empty chunk as
                    its own paragraph. Keeps styling consistent without a
                    full markdown engine. */}
                <HowToStartBody text={howToStart} />
              </DetailBlock>
            )}
          </div>
        </div>
      </section>

      {/* ------------- Related tools ------------- */}
      {related.length > 0 && (
        <section
          className="sas-section-sm border-t border-[color:var(--sas-line)]"
          data-testid="tool-detail-related"
        >
          <div className="sas-container">
            <div className="max-w-5xl mx-auto">
              <div className="sas-eyebrow mb-2">{l.relatedTitle}</div>
              <h2 className="sas-h2">{primaryCatLabel}</h2>
              <p className="sas-body mt-2 text-[14.5px] max-w-2xl">{l.relatedDesc}</p>

              <div className="sas-grid-2 mt-8">
                {related.map((r) => (
                  <RelatedToolCard
                    key={r.slug}
                    tool={r}
                    lang={lang}
                    l={l}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ------------- Footer back link ------------- */}
      <section
        className="sas-section-sm border-t border-[color:var(--sas-line)]"
        data-testid="tool-detail-footer"
      >
        <div className="sas-container">
          <LocalizedLink
            to="/tools"
            className="sas-btn-link"
            data-testid="tool-detail-back"
          >
            <BackIcon size={16} aria-hidden /> {l.back}
          </LocalizedLink>
        </div>
      </section>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function DetailBlock({ icon, title, children, testId }) {
  return (
    <section data-testid={testId}>
      <h2 className="flex items-center gap-2 sas-h3 text-[color:var(--sas-ink)]">
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-lg"
          style={{
            background: "var(--sas-accent-soft)",
            color: "var(--sas-accent)",
          }}
          aria-hidden
        >
          {icon}
        </span>
        <span>{title}</span>
      </h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function HowToStartBody({ text }) {
  // Split on blank lines OR consecutive newlines so admins can write
  // either a single paragraph, separate paragraphs, or a quick numbered
  // list ("1. Sign up\n2. Try a prompt") and the layout adapts.
  const chunks = (text || "")
    .split(/\n{2,}|\r\n{2,}/g)
    .map((s) => s.trim())
    .filter(Boolean);

  if (chunks.length <= 1) {
    return (
      <p className="sas-body text-[15.5px] leading-relaxed whitespace-pre-line">
        {text}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {chunks.map((c, i) => (
        <p
          key={i}
          className="sas-body text-[15.5px] leading-relaxed whitespace-pre-line"
        >
          {c}
        </p>
      ))}
    </div>
  );
}

function ScreenshotFrame({ src, alt, l }) {
  const [errored, setErrored] = useState(false);

  if (errored) {
    return (
      <div
        className="rounded-2xl border border-dashed border-[color:var(--sas-line)] bg-[color:var(--sas-accent-soft)]/20 aspect-[16/9] flex flex-col items-center justify-center gap-2 text-[color:var(--sas-ink-2)]"
        data-testid="tool-detail-screenshot-fallback"
      >
        <ImageOff size={28} aria-hidden />
        <span className="text-sm">{l.coverFallbackText}</span>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl overflow-hidden border border-[color:var(--sas-line)] shadow-sm bg-[color:var(--sas-line)]/40"
      data-testid="tool-detail-screenshot"
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setErrored(true)}
        className="block w-full h-auto object-cover"
      />
    </div>
  );
}

function RelatedToolCard({ tool, lang, l }) {
  const arName = getToolNameAr(tool.slug);
  const showAr = !!arName && arName !== tool.name;
  const desc = pickLang(tool.desc, lang);

  return (
    <LocalizedLink
      to={`/tools/${tool.slug}`}
      className="sas-card group hover:border-[color:var(--sas-accent)]/40 transition-colors flex flex-col"
      data-testid={`tool-detail-related-${tool.slug}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <ToolLogo tool={tool} size={40} testId={`tool-detail-related-logo-${tool.slug}`} />
        <div className="flex-1 min-w-0">
          <h3
            className="text-[15.5px] font-semibold leading-tight text-[color:var(--sas-ink)]"
            dir="ltr"
          >
            {tool.name}
          </h3>
          {showAr && (
            <div
              className="text-[12.5px] text-[color:var(--sas-ink-2)] mt-0.5"
              dir="rtl"
              lang="ar"
            >
              {arName}
            </div>
          )}
        </div>
      </div>
      {desc && (
        <p className="text-[13.5px] leading-[1.55] text-[color:var(--sas-ink-2)] line-clamp-2">
          {desc}
        </p>
      )}
      <span className="mt-4 text-[12.5px] font-semibold text-[color:var(--sas-accent)] group-hover:underline self-start">
        {l.tryCta} →
      </span>
    </LocalizedLink>
  );
}
